import React, { useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  notification,
  Row,
  Col,
} from "antd";
import { useQuery, useMutation } from "@tanstack/react-query";
import { inviteUser } from "../services/projectsAPI";
import { fetchVendors } from "../services/vendorsAPI";
import { useAuth } from "../hooks/useAuth";
import queryClient from "../queryClient";
import {
  filterOption,
  formatPhoneNumber,
  handlePhoneNumberKeyDown,
} from "../utils/textUtils";
import CreateVendorModal from "./CreateVendorModal";
import ProjectSettingToggle from "./ProjectSettingToggle";
import { PlusOutlined } from "@ant-design/icons";

const { Option } = Select;

const CreateUserModal = ({ visible, onClose, onUserCreated }) => {
  const { selectedProject } = useAuth();
  const projectId = selectedProject.project_id;
  const [isCreateVendorModalVisible, setIsCreateVendorModalVisible] =
    useState(false);
  const [form] = Form.useForm();

  const { data: vendors } = useQuery({
    queryKey: ["vendors", projectId, 1, 1000],
    queryFn: () => fetchVendors(projectId, 1, 1000),
    enabled: !!projectId,
  });

  const mutation = useMutation({
    mutationFn: inviteUser,
    onSuccess: (_data, variables) => {
      const isSendingInvitationEmail =
        variables?.send_invitation_email !== false;
      notification.success({
        message: isSendingInvitationEmail
          ? "User invited successfully"
          : "User added successfully",
      });
      queryClient.invalidateQueries(["projectUsers", projectId]);
      onUserCreated?.(variables?.email);
      onClose();
      form.resetFields();
    },
    onError: (error, variables) => {
      const isSendingInvitationEmail =
        variables?.send_invitation_email !== false;
      const errorDetail = error.response?.data?.detail;
      const errorMessage =
        typeof errorDetail === "string"
          ? errorDetail
          : errorDetail?.message ||
            error.message ||
            "An unknown error occurred";
      notification.error({
        message: isSendingInvitationEmail
          ? "Failed to invite user"
          : "Failed to add user",
        description: errorMessage,
      });
    },
  });

  const handleSetVendor = (value) => {
    form.setFieldsValue({ vendor_id: value });
  };

  const handleInviteUser = (values) => {
    const invitationData = {
      ...values,
      project_id: selectedProject.project_id,
    };
    mutation.mutate(invitationData);
  };

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  return (
    <>
      <Modal
        title="Invite User"
        open={visible}
        onCancel={handleClose}
        footer={null}
        centered
      >
        <Form form={form} onFinish={handleInviteUser} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="First Name"
                name="first_name"
                rules={[
                  { required: true, message: "Please input the first name!" },
                ]}
                validateTrigger="onFinish"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Last Name"
                name="last_name"
                rules={[
                  { required: true, message: "Please input the last name!" },
                ]}
                validateTrigger="onFinish"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: "Please input the email!" },
                  { type: "email", message: "Please enter a valid email!" },
                ]}
                validateTrigger="onFinish"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Phone Number" name="phone_number">
                <Input
                  onKeyDown={handlePhoneNumberKeyDown}
                  onChange={(e) => {
                    const formatted = formatPhoneNumber(e.target.value);
                    form.setFieldsValue({ phone_number: formatted });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Role"
                name="role"
                rules={[{ required: true, message: "Please select a role!" }]}
              >
                <Select placeholder="Select a role">
                  <Option value="project_manager">Project Manager</Option>
                  <Option value="field">Field</Option>
                  <Option value="consultant">Consultant</Option>
                  <Option value="subcontractor">Subcontractor</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Vendor" name="vendor_id">
                <Select
                  showSearch
                  optionFilterProp="children"
                  filterOption={filterOption}
                  placeholder="Select a vendor"
                  options={vendors?.vendors?.map((vendor) => ({
                    value: vendor.id,
                    label: vendor.name,
                  }))}
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <Button
                        type="text"
                        icon={<PlusOutlined />}
                        onClick={() => setIsCreateVendorModalVisible(true)}
                        className="w-full my-2"
                      >
                        Create Vendor
                      </Button>
                    </>
                  )}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Notes" name="notes">
            <Input.TextArea rows={4} />
          </Form.Item>

          <div className="project-settings-grid">
            <ProjectSettingToggle
              name="send_invitation_email"
              title="Send Invitation Email"
              initialValue={true}
            />
          </div>

          {/* Junk/Spam Warning Note */}
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.send_invitation_email !==
              currentValues.send_invitation_email
            }
          >
            {({ getFieldValue }) =>
              getFieldValue("send_invitation_email") !== false && (
                <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg mb-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-amber-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-semibold text-amber-800 mb-1">
                        Important Note
                      </h4>
                      <p className="text-sm text-amber-700 leading-relaxed">
                        Please inform the invited user to check their spam
                        folder and mark our emails as "Not Spam" to ensure they
                        receive project communications in their inbox going
                        forward.
                      </p>
                    </div>
                  </div>
                </div>
              )
            }
          </Form.Item>

          <Form.Item
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.send_invitation_email !==
              currentValues.send_invitation_email
            }
          >
            {({ getFieldValue }) => (
              <Button
                type="primary"
                htmlType="submit"
                loading={mutation.isPending}
              >
                {getFieldValue("send_invitation_email") !== false
                  ? "Invite User"
                  : "Add User"}
              </Button>
            )}
          </Form.Item>
        </Form>
      </Modal>

      <CreateVendorModal
        visible={isCreateVendorModalVisible}
        onClose={() => setIsCreateVendorModalVisible(false)}
        handleSetVendor={handleSetVendor}
      />
    </>
  );
};

export default CreateUserModal;
